import * as Yup from "yup";
import { FeeStatus, RegistrationType } from "../../Base/AdoptionConstants";
import AdopteeInfoForm from "./FormSteps/AdopteeInfoForm";
import AdoptiveParentsInfoForm from "./FormSteps/AdoptiveParentsInfoForm";
import BirthParentsInfoForm from "./FormSteps/BirthParentsInfoForm";
import IdentifyingInfoForm from "./FormSteps/IdentifyingInfoForm";
import { MinorSiblingIneligibleRegistration, UnderAgeIneligibleRegistration } from "./FormSteps/IneligibleRegistration";
import PaymentConfirmationForm from "./FormSteps/PaymentConfirmationForm";
import RegistrationInfoForm from "./FormSteps/RegistrationInfoForm";

Yup.addMethod(Yup.mixed, "fileIsImageOrPdf", function (message) {
  return this.test("file-is-image-or-pdf", message || "File must be an image or PDF", function (valueToTest) {
    // Unselected file value is acceptable
    if (!valueToTest) {
      return true;
    }

    // If this is a file already in our DB, allow it
    if (valueToTest.registrationDocumentId) {
      return true;
    }

    const fileType = valueToTest.type;

    // If file is image or pdf, mark as valid
    if (fileType && (fileType.startsWith("image/") || fileType === "application/pdf")) {
      return true;
    }
    return false;
  });
});

const registrationInfoValidationSchema = Yup.object().shape({
  registrantContactInfo: Yup.object().shape({
    firstName: Yup.string().nullable().required("This field is required"),
    lastName: Yup.string().nullable().required("This field is required"),
    email: Yup.string().email("Email address is invalid").nullable().required("This field is required"),
    streetAddress1: Yup.string().nullable().required("This field is required"),
  }),
});
const identifyingInfoValidationSchema = Yup.object()
  .shape({
    receiveMatchInfo: Yup.boolean().nullable().required("This field is required"),
    releaseBirthCertificate: Yup.boolean()
      .nullable()
      .when("registrationType", {
        is: (registrationType) => registrationType === RegistrationType.BIRTH_PARENT.value,
        then: Yup.boolean().nullable().required("This field is required"),
      }),
    shareMedicalHistory: Yup.boolean()
      .nullable()
      .when("registrationType", {
        is: (registrationType) => registrationType === RegistrationType.BIRTH_PARENT.value,
        then: Yup.boolean().nullable().required("This field is required"),
      }),
    shareContactInfo: Yup.string().nullable().required("You must specify whether or not to share your information"),
    contactInformation: Yup.object()
      .nullable()
      .shape({
        // Require full name when share contact info is chosen
        firstName: Yup.string().test(
          "first-name-required-sharing-info",
          "This field is required",
          function (firstName) {
            // Since this is a nested field, form values are stored in from[1].value
            const baseFormValues = this.from[1].value;
            return Boolean(firstName || !["yes", "intermediary"].includes(baseFormValues.shareContactInfo));
          }
        ),
        lastName: Yup.string().test("last-name-required-sharing-info", "This field is required", function (lastName) {
          // Since this is a nested field, form values are stored in from[1].value
          const baseFormValues = this.from[1].value;
          return Boolean(lastName || !["yes", "intermediary"].includes(baseFormValues.shareContactInfo));
        }),
        email: Yup.string().email("Email address is invalid"),
      }),
  })
  .test(
    "at-least-one-contact-info",
    "You have selected to share contact information - please enter the contact information you would like to share",
    function (formValues) {
      const hasChosenToShareInfo = ["yes", "intermediary"].includes(formValues.shareContactInfo);
      if (!hasChosenToShareInfo) {
        return true;
      }
      const hasSharedEmail = Boolean(formValues.contactInformation.email);
      const hasSharedPhone = Boolean(formValues.contactInformation.phone);
      const hasSharedAddress = Boolean(
        formValues.contactInformation.streetAddress1 && formValues.contactInformation.city
      );
      const hasEnteredContactInfo = hasSharedAddress || hasSharedEmail || hasSharedPhone;
      if (!hasEnteredContactInfo) {
        return this.createError({
          message:
            "You have selected to share contact information - please enter contact the information you would like to share",
          path: "contactInfoToShare",
        });
      }
      return true;
    }
  );
const adopteeValidationSchema = Yup.object()
  .shape({
    noAdoptedMinorSiblingsInHousehold: Yup.boolean().required("An answer to this question is required").nullable(),
    birthDate: Yup.date()
      .nullable()
      .when("knowExactBirthDate", {
        is: (knowExactBirthDate) => knowExactBirthDate === "yes",
        then: Yup.date().nullable().required("This field is required"),
      }),
    knowExactBirthDate: Yup.string().nullable().required("This field is required"),
    birthDateRangeStart: Yup.date()
      .nullable()
      .when("knowExactBirthDate", {
        is: (knowExactBirthDate) => knowExactBirthDate === "no",
        then: Yup.date().nullable().required("This field is required"),
      }),
    birthDateRangeEnd: Yup.date()
      .nullable()
      .when("knowExactBirthDate", {
        is: (knowExactBirthDate) => knowExactBirthDate === "no",
        then: Yup.date().nullable().required("This field is required"),
      })
      .test("end-date-is-later-than-start", "The end date must be later than the start date", function (endDate) {
        if (!this.parent.birthDateRangeStart || !endDate) {
          return true;
        }
        return endDate > this.parent.birthDateRangeStart;
      }),
    birthSex: Yup.string().required("This field is required"),
  })
  .test(
    "adoptee-birth-location-required",
    "You must enter some information about the birth location. Please specify a city, hospital or provide any other birth location information.",
    function (values) {
      const hasSpecifiedCity = values.birthCity && values.birthCity !== "Unknown / Other";
      const hasSpecifiedHospital = values.birthHospital && values.birthHospital !== "Unknown / Other";
      const hasSpecifiedOtherLocation = values.otherBirthLocation;

      if (hasSpecifiedCity || hasSpecifiedHospital || hasSpecifiedOtherLocation) {
        return true;
      }

      return this.createError({
        message:
          "You must enter some information about the birth location. Please specify a city, hospital or provide any other birth location information.",
        path: "adopteeBirthLocation",
      });
    }
  );

const adoptiveParentsValidationSchema = Yup.object().shape({});
const birthParentsValidationSchema = Yup.object().shape({});
const paymentConfirmationValidationSchema = Yup.object()
  .shape({
    verifyIdentityOption: Yup.string().required("Please select an option"),
    copyOfId1: Yup.mixed().fileIsImageOrPdf(),
    copyOfId2: Yup.mixed().fileIsImageOrPdf(),
    agreeToDisclaimer: Yup.boolean().isTrue("You must check this box to agree to the disclaimer in order to submit"),
    agreeToUpdateFee: Yup.boolean().when("requireUpdateFee", {
      is: true,
      then: Yup.boolean().isTrue("You must check this box to proceed."),
    }),
    paymentOption: Yup.string().when("feeStatus", {
      // Payment only required when no fee status set up OR the payment option is pending
      is: (feeStatus) =>
        !feeStatus || [FeeStatus.FEE_PENDING.value, FeeStatus.PREV_PAID_PENDING.value].includes(feeStatus),
      then: Yup.string().required("Please select an option"),
    }),
  })
  .test("at-least-one-id-required", "Please select a file to upload a copy of your ID.", function (formValues) {
    if (
      formValues.verifyIdentityOption === "upload" &&
      !Boolean(formValues.copyOfId1) &&
      !Boolean(formValues.copyOfId2)
    ) {
      return this.createError({
        message: "Please select a file to upload a copy of your ID.",
        path: "identificationDocuments",
      });
    }
    return true;
  });

export const AdoptionRegistrationSteps = {
  RegistrationInfo: {
    title: "Information About You",
    component: RegistrationInfoForm,
    validationSchema: registrationInfoValidationSchema,
    path: "/registration-info",
  },
  IdentifyingInfo: {
    title: "Identifying Information",
    component: IdentifyingInfoForm,
    validationSchema: identifyingInfoValidationSchema,
    path: "/contact-info",
  },
  AdopteeInfo: {
    title: "Adoptee Information",
    component: AdopteeInfoForm,
    validationSchema: adopteeValidationSchema,
    path: "/adoptee-info",
  },
  AdoptiveParents: {
    title: "Adoptive Parents",
    component: AdoptiveParentsInfoForm,
    validationSchema: adoptiveParentsValidationSchema,
    path: "/adoptive-parents-info",
  },
  BirthParents: {
    title: "Birth Parents",
    component: BirthParentsInfoForm,
    validationSchema: birthParentsValidationSchema,
    path: "/birth-parents-info",
  },
  PaymentConfirmation: {
    title: "Payment & Confirmation",
    component: PaymentConfirmationForm,
    validationSchema: paymentConfirmationValidationSchema,
    path: "/payment-confirmation",
  },
};

export const AdoptionRegistrationIneligiblePages = {
  UnderAgeIneligibleRegistration: {
    title: "",
    component: UnderAgeIneligibleRegistration,
    validationSchema: Yup.object(),
    path: "/underage-ineligible-registration",
  },
  MinorSiblingIneligibleRegistration: {
    title: "",
    component: MinorSiblingIneligibleRegistration,
    validationSchema: Yup.object(),
    path: "/minor-sibling-ineligible-registration",
  },
};
